import { useLocation, useNavigate } from 'react-router-dom';
import { useColorMode } from '@chakra-ui/react';
import { t } from 'i18next';

import { useQueryParams } from '../../../utils/useQueryParams';
import { openNewTab } from '../../../utils/UIUtils';

import StickyHeader from '../../../components/StickyHeader';

import { FixedFill, MapFill, VerfiedFill, Xmark } from '@peacecover/icon';
import { Button } from '@peacecover/component';
import { BaseFootprint } from '../../../domain/models/locations/BaseFootprint';

const AlreadyReportedFootprintPage = () => {
  const { footprint, lotAddress } = useLocation().state;

  const navigate = useNavigate();
  const { queryParams, removeQueryParams, updateQueryParams } = useQueryParams();
  const locationName = queryParams.get('locationName');
  const locationId = queryParams.get('locationId');
  
  const { colorMode } = useColorMode();

  // React Router state로 전달되며 클래스 프로토타입이 사라진 경우를 복구
  const baseFootprint = Object.setPrototypeOf(footprint, BaseFootprint.prototype) as BaseFootprint;

  const handleNavigateToCreateEvent = () => {
    removeQueryParams(
      ['locationId', 'locationName', 'spotsCategory', 'kakaoUID'],
      '/report-location/spots'
    );
  };

  const handleNavigateToEventDetail = () => {
    openNewTab(`/locations/${locationId}`);
  };

  const handleNavigateToEditEvent = () => {
    updateQueryParams(
      {
        footprintId: baseFootprint.footprintId
      },
      '/report-location/footprints-category'
    );
  };

  return (
    <>
      <StickyHeader
        title={t('AlreadyReportedEventPage.pageTitle', { defaultValue: '제보하기' }) as string}
        alwaysShowTitle={true}
        showTitle={true}
        leftContent={
          <Xmark
            size={24}
            className="text-text-main-lv1"
            onClick={() => navigate(`/report-location/footprints?${queryParams.toString()}`)}
          />
        }
      />
      <div
        className="flex flex-col min-h-screen items-center pt-[calc(54px+env(safe-area-inset-top))] pb-[calc(128px+env(safe-area-inset-bottom))] bg-repeat"
        style={{
          backgroundImage: `url('/assets/backgrounds/report/report_complete_${colorMode === 'dark' ? 'dark' : 'light'}.png')`,
          backgroundRepeat: 'round'
        }}
      >
        <div className="flex flex-col gap-[16px] items-center mt-[57px]">
          <h1 className="text-[26px] font-bold text-text-main-lv1">
            {t('AlreadyReportedEventPage.title', {
              defaultValue: '정보가 다른가요?'
            })}
          </h1>
          <p className="text-[15px] text-text-main-lv3 leading-tight text-center whitespace-pre-wrap">
            {t('AlreadyReportedEventPage.description', {
              defaultValue:
                '정보가 잘못됐거나, 새로운 내용을 추가하고 싶으면 \n팬하트에 알려주세요.'
            })}
          </p>
        </div>
        <FootprintCardWithLocation
          footprint={baseFootprint}
          locationName={locationName || ''}
          lotAddress={lotAddress || ''}
        />
        <div className="fixed mb-[calc(10px+env(safe-area-inset-bottom))] flex flex-col gap-[8px] bottom-0 px-[20px] py-[10px] w-full">
          <Button
            size="large"
            className="w-full text-[17px] font-semibold"
            onClick={handleNavigateToEventDetail}
          >
            {t('AlreadyReportedEventPage.viewEvent', {
              defaultValue: '해당 {{spotsCategory}} 자세히보기',
              spotsCategory: t('AlreadyReportedEventPage.place')
            })}
          </Button>
          <Button
            size="large"
            btnColor="secondary"
            className="w-full text-[17px] font-semibold"
            onClick={handleNavigateToEditEvent}
          >
            {t('AlreadyReportedEventPage.editEvent', {
              defaultValue: '정보 수정 요청하기'
            })}
          </Button>
          <Button
            size="large"
            btnColor="secondary"
            className="w-full text-[17px] font-semibold"
            onClick={handleNavigateToCreateEvent}
          >
            {t('AlreadyReportedEventPage.createEvent', {
              defaultValue: '다른 {{spotsCategory}} 등록하기',
              spotsCategory: t('AlreadyReportedEventPage.place')
            })}
          </Button>
        </div>
      </div>
    </>
  );
};

export default AlreadyReportedFootprintPage;

const FootprintCardWithLocation = ({
  footprint,
  locationName,
  lotAddress
}: {
  footprint: BaseFootprint;
  locationName: string;
  lotAddress: string;
}) => {
  return (
    <div className="rounded-[12px] bg-background-light dark:bg-background-dark dark:border dark:border-separator2 mt-[49px] w-[327px] shadow-[0_0_0_1px_rgba(0,0,0,0.01),0_0_14px_0_rgba(0,0,0,0.08)]">
      <div className="flex flex-col gap-[5px] px-[16px] py-[14px] border-b border-separator1 dark:border-separator2">
        <div className="flex items-center gap-[2px]">
          <MapFill size={14} className="text-on-theirty" />
          <p className="text-[17px] font-semibold text-text-main-lv2 leading-[20px]">
            {locationName}
          </p>
        </div>
        <p className="h-[16px] text-[13px] text-text-main-lv4 leading-[16px]">{lotAddress}</p>
      </div>
      <div className="flex flex-row items-center w-full justify-between gap-[12px] px-[16px] pt-[12px] pb-[16px]">
        <div className="flex flex-col gap-[6px]">
          {footprint.contentType === 'official_content' && (
            <div className="w-full flex items-center gap-[4px]">
              <VerfiedFill size={14} className="text-on-varified" />
              <p className="font-medium text-[12px] text-on-varified">
                {t('AlreadyReportedEventPage.officialContent', {
                  defaultValue: '공식 컨텐츠에 나왔어요'
                })}
              </p>
            </div>
          )}
          {footprint.description && (
            <p className="text-left text-[15px] text-text-main-lv2 whitespace-pre-wrap max-w-[200px] line-clamp-2 tracking-[0]">
              {footprint.description}
            </p>
          )}
        </div>
        {footprint.baseThumbnailUrl ? (
          <img
            src={footprint.baseThumbnailUrl}
            alt="Footprint"
            className="w-[83px] h-[60px] object-cover rounded-[10px]"
          />
        ) : (
          <div className="w-[83px] h-[60px] rounded-[10px] bg-gray-100 flex items-center justify-center">
            <FixedFill size={16} className="text-text-sub-lv1" />
          </div>
        )}
      </div>
    </div>
  );
};